/**
 * Authentication Composable with Token Support
 *
 * Secure authentication using Laravel Sanctum with:
 * - Bearer token authentication for cross-domain deployments
 * - HttpOnly cookies (fallback for same-domain)
 * - CSRF token protection
 * - Automatic session refresh
 *
 * @see docs/AUTHENTICATION_SECURITY_ASSESSMENT.md
 */

// Token storage key
const AUTH_TOKEN_KEY = 'admin_auth_token'

/**
 * Get stored auth token (session storage for security)
 */
export const getAuthToken = (): string | null => {
  if (process.client) {
    return sessionStorage.getItem(AUTH_TOKEN_KEY)
  }
  return null
}

/**
 * Store auth token in session storage
 */
export const setAuthToken = (token: string): void => {
  if (process.client) {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token)
  }
}

/**
 * Clear auth token
 */
export const clearAuthToken = (): void => {
  if (process.client) {
    sessionStorage.removeItem(AUTH_TOKEN_KEY)
  }
}

export const useHttpOnlyAuth = () => {
  const { $api } = useNuxtApp()
  const config = useRuntimeConfig()

  /**
   * Initialize CSRF protection by fetching CSRF cookie
   * Must be called before any authenticated requests
   */
  const initializeCsrf = async (): Promise<boolean> => {
    try {
      // Get Laravel base URL (without /api/v1)
      // In development with proxy: config.public.apiBase is '/api/v1', so we use '' for relative
      // In production: config.public.apiBase is 'https://api.example.com/api/v1', extract base
      const apiBase = config.public.apiBase || '/api/v1'
      const laravelBaseUrl = apiBase.startsWith('/')
        ? '' // Relative path for dev proxy
        : apiBase.replace('/api/v1', '')

      await $fetch(`${laravelBaseUrl}/sanctum/csrf-cookie`, {
        credentials: 'include'
      })
      return true
    } catch (error) {
      console.error('Failed to initialize CSRF protection:', error)
      return false
    }
  }

  /**
   * Login with email and password
   * Stores Bearer token for cross-domain authentication
   */
  const login = async (credentials: { email: string; password: string; remember?: boolean }) => {
    try {
      // Initialize CSRF token first
      await initializeCsrf()

      // Perform login - returns token for cross-domain auth
      const response = await $api.post('/admin/auth/login', credentials) as { 
        data?: { user?: unknown; token?: string }
        token?: string
        message?: string 
      }

      // Extract token from response (check both root and nested data)
      const token = response.token || response.data?.token
      if (token) {
        setAuthToken(token)
      }

      return {
        success: true,
        user: response.data?.user,
        message: response.message
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
        errors: error.response?.data?.errors
      }
    }
  }

  /**
   * Logout and invalidate session
   * Clears auth token and server-side session
   */
  const logout = async (): Promise<boolean> => {
    try {
      await $api.post('/admin/auth/logout')

      // Clear token and any client-side state
      clearAuthToken()
      if (process.client) {
        sessionStorage.clear()
      }

      return true
    } catch (error) {
      console.error('Logout failed:', error)
      // Still clear local state on error
      clearAuthToken()
      return false
    }
  }

  /**
   * Refresh session to extend expiration
   * Regenerates session ID for security
   */
  const refresh = async () => {
    try {
      const response = await $api.post('/admin/auth/refresh')
      return {
        success: true,
        user: response.data?.user
      }
    } catch (error: any) {
      console.error('Session refresh failed:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Refresh failed'
      }
    }
  }

  /**
   * Get current authenticated user
   * Cookie is sent automatically with request
   */
  const getCurrentUser = async () => {
    try {
      const response = await $api.get('/admin/auth/me')
      return {
        success: true,
        user: response.data
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get user'
      }
    }
  }

  /**
   * Check if user is authenticated
   * Verifies session validity on server
   */
  const checkAuth = async (): Promise<boolean> => {
    try {
      const response = await $api.get('/admin/auth/check')
      return response.authenticated === true
    } catch (error) {
      return false
    }
  }

  /**
   * Auto-refresh session on user activity
   * Call this periodically to keep session alive
   */
  const refreshOnActivity = async (): Promise<void> => {
    try {
      await refresh()
    } catch (error) {
      console.error('Activity-based refresh failed:', error)
    }
  }

  /**
   * Setup activity-based session refresh
   * Refreshes session after 30 seconds of inactivity
   */
  const setupActivityRefresh = () => {
    if (!process.client) return

    let activityTimeout: NodeJS.Timeout | null = null
    const INACTIVITY_PERIOD = 30000 // 30 seconds
    const REFRESH_INTERVAL = 3600000 // 1 hour

    let lastRefresh = Date.now()

    const handleActivity = () => {
      if (activityTimeout) clearTimeout(activityTimeout)

      activityTimeout = setTimeout(async () => {
        const elapsed = Date.now() - lastRefresh

        // Refresh if more than 1 hour since last refresh
        if (elapsed > REFRESH_INTERVAL) {
          const result = await refresh()
          if (result.success) {
            lastRefresh = Date.now()
          }
        }
      }, INACTIVITY_PERIOD)
    }

    // Listen to user activity
    document.addEventListener('mousemove', handleActivity)
    document.addEventListener('keypress', handleActivity)
    document.addEventListener('click', handleActivity)
    document.addEventListener('scroll', handleActivity)

    // Cleanup function
    return () => {
      if (activityTimeout) clearTimeout(activityTimeout)
      document.removeEventListener('mousemove', handleActivity)
      document.removeEventListener('keypress', handleActivity)
      document.removeEventListener('click', handleActivity)
      document.removeEventListener('scroll', handleActivity)
    }
  }

  return {
    initializeCsrf,
    login,
    logout,
    refresh,
    getCurrentUser,
    checkAuth,
    refreshOnActivity,
    setupActivityRefresh
  }
}
